import { useState, useCallback } from "react";
import axios from "axios";
import { SearchResponse, SearchRequest } from "../types";

export interface UseSearchReturn {
  results: SearchResponse | null;
  loading: boolean;
  error: string | null;
  search: (
    query: string,
    page?: number,
    pageSize?: number,
    append?: boolean
  ) => Promise<SearchResponse | null>;
  clearResults: () => void;
}

export const useSearch = (): UseSearchReturn => {
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (
      query: string,
      page: number = 1,
      pageSize: number = 10,
      append: boolean = false
    ) => {
      if (!query || !query.trim()) {
        setError("Please enter a search query");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const requestData: SearchRequest = {
          description: query,
        };

        const response = await axios.post<SearchResponse>(
          `/search?page=${page}&page_size=${pageSize}`,
          requestData
        );

        if (append) {
          setResults((prev) => {
            if (!prev) return response.data;
            return {
              ...response.data,
              results: [...prev.results, ...response.data.results],
            };
          });
        } else {
          setResults(response.data);
        }
        return response.data;
      } catch (err: any) {
        if (axios.isAxiosError(err)) {
          setError(err.response?.data?.error || err.message);
        } else {
          setError("An unexpected error occurred");
        }
        setResults(null);
      } finally {
        setLoading(false);
      }
      return null;
    },
    []
  );

  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);

  return { results, loading, error, search, clearResults };
};
